import React from "react";
import { useQuery } from "@apollo/client";
import { Reservation } from "@types";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Layout from "components/Layout";
import TimelineView from "components/TimelineView";
import ScheduleDelivery from "components/Delivery/ScheduleDelivery";
import LoadingScreen from "components/LoadingScreen";
import { DeliveryFlowStep } from "components/Delivery/ScheduleDelivery/deliveryFlow";
import useCancelClientReservation from "components/Reservation/useCancelClientReservation";
import ClientOrderQuery from "./ClientOrderQuery.gql";
import ClientPortalConfigQuery from "./ClientPortalConfigQuery.gql";
import ClientReservationQuery from "pages/Reservations/ClientReservationQuery.gql";
import { ORDER_STATUS } from "utils/constants";

function parseTime(timeStr: string): Date {
  const [hours, minutes, seconds] = timeStr.split(":").map(Number);
  const date = new Date();
  date.setHours(hours);
  date.setMinutes(minutes);
  date.setSeconds(seconds);
  return date;
}

const Delivery = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("xct") || "";
  const initialStep = (
    location.state as { deliveryStep?: DeliveryFlowStep } | null
  )?.deliveryStep;

  const { loading, data: clientOrderData } = useQuery(ClientOrderQuery, {
    fetchPolicy: "network-only",
  });

  const { loading: configLoading, data: clientPortalConfigData } = useQuery(
    ClientPortalConfigQuery,
    {
      fetchPolicy: "network-only",
    },
  );

  const tokenState = clientPortalConfigData?.clientPortalConfig?.tokenState;
  const hasReservation = tokenState?.hasReservation ?? false;

  const {
    loading: reservationLoading,
    data: reservationData,
    refetch: refetchReservation,
  } = useQuery(ClientReservationQuery, {
    fetchPolicy: "network-only",
    skip: !hasReservation,
  });
  const { cancelClientReservation, loading: cancelLoading } =
    useCancelClientReservation();

  const isLoading = React.useMemo<boolean>(() => {
    return loading || configLoading || (hasReservation && reservationLoading);
  }, [loading, configLoading, hasReservation, reservationLoading]);

  // Redirect reservation-only tokens to the reservations page
  React.useEffect(() => {
    if (
      !isLoading &&
      tokenState &&
      !tokenState.hasOrder &&
      tokenState.hasReservation &&
      token
    ) {
      navigate(`/reservations?xct=${encodeURIComponent(token)}`, {
        replace: true,
      });
    }
  }, [isLoading, tokenState, token, navigate]);

  const isOrderCancelled = React.useMemo<boolean>(
    () => clientOrderData?.clientOrder?.status === ORDER_STATUS.CANCELLED,
    [clientOrderData],
  );
  const isOrderWaitingForStock = React.useMemo<boolean>(
    () => clientOrderData?.clientOrder?.is_waiting_for_stock,
    [clientOrderData],
  );
  let isTimelineDisplayed =
    clientOrderData?.clientOrder?.delivery?.status &&
    clientOrderData?.clientOrder?.status === ORDER_STATUS.VALIDATED;

  if (
    clientOrderData?.clientOrder?.timeSlot?.date &&
    clientPortalConfigData?.clientPortalConfig?.contractorConfig?.order_gap &&
    clientPortalConfigData?.clientPortalConfig?.contractorConfig
      ?.next_day_shift_hour
  ) {
    const deliveryDate = new Date(clientOrderData?.clientOrder?.timeSlot.date);
    const orderGapDays =
      clientPortalConfigData?.clientPortalConfig?.contractorConfig?.order_gap;
    const nextDayShiftHours = parseTime(
      clientPortalConfigData?.clientPortalConfig?.contractorConfig
        ?.next_day_shift_hour,
    );
    let cutOff = new Date(clientOrderData?.clientOrder?.timeSlot.date);
    // Subtract the order gap days
    cutOff.setDate(deliveryDate.getDate() - orderGapDays);

    // Add the next day shift hours
    cutOff.setHours(nextDayShiftHours.getHours());
    cutOff.setMinutes(0);

    // Check if the current date and time is past the calculated date and time
    if (new Date() > cutOff) {
      isTimelineDisplayed = true;
    }
  }

  const [reservationState, setReservationState] =
    React.useState<Reservation | null>(
      reservationData?.clientReservation ?? null,
    );

  React.useEffect(() => {
    setReservationState(reservationData?.clientReservation ?? null);
  }, [reservationData]);

  const reservation =
    reservationState ?? reservationData?.clientReservation ?? null;

  const handleReservationExpire = React.useCallback(async () => {
    const result = await refetchReservation();
    setReservationState(result.data?.clientReservation ?? null);
  }, [refetchReservation]);

  const handleCancelReservation = React.useCallback(async () => {
    const updatedReservation = await cancelClientReservation();
    setReservationState(updatedReservation);
  }, [cancelClientReservation]);

  const handleReservationUpdate = React.useCallback(
    (updatedReservation: Reservation) => {
      setReservationState(updatedReservation);
    },
    [],
  );

  if (
    isLoading ||
    !clientOrderData ||
    !clientPortalConfigData ||
    !clientOrderData.clientOrder ||
    !clientPortalConfigData.clientPortalConfig
  ) {
    return <LoadingScreen />;
  }

  const officeConfig = clientPortalConfigData.clientPortalConfig.officeConfig;
  const contractorConfig =
    clientPortalConfigData.clientPortalConfig.contractorConfig;

  if (isOrderCancelled) {
    return (
      <Layout
        order={clientOrderData.clientOrder}
        clientPortalConfig={clientPortalConfigData.clientPortalConfig}
      >
        <div className="cancelled">
          La livraison a été annulée, merci de vous rapprocher de votre magasin
        </div>
      </Layout>
    );
  } else if (isOrderWaitingForStock) {
    return (
      <Layout
        order={clientOrderData.clientOrder}
        clientPortalConfig={clientPortalConfigData.clientPortalConfig}
      >
        <div className="cancelled">
          Un ou plusieurs produits de votre commande ne sont pas disponibles,
          merci de vous rapprocher de votre magasin{" "}
          {clientOrderData.clientOrder.store_name} pour obtenir plus de
          renseignements
        </div>
      </Layout>
    );
  } else {
    return (
      <Layout
        order={clientOrderData.clientOrder}
        clientPortalConfig={clientPortalConfigData.clientPortalConfig}
      >
        <div className="delivery">
          {isTimelineDisplayed ? (
            <TimelineView delivery={clientOrderData.clientOrder.delivery} />
          ) : (
            <ScheduleDelivery
              order={clientOrderData.clientOrder}
              hasReservation={hasReservation}
              reservation={reservation}
              officeConfig={officeConfig}
              contractorConfig={contractorConfig}
              onReservationExpire={handleReservationExpire}
              onReservationUpdate={handleReservationUpdate}
              onCancelReservation={handleCancelReservation}
              isCancellingReservation={cancelLoading}
              initialStep={initialStep}
              initialStepKey={location.key}
            />
          )}
        </div>
      </Layout>
    );
  }
};

export default Delivery;
